import React, { useMemo } from 'react';
import { Box, Button, Chip, Grid, Stack, Typography } from '@mui/material';
import {
  DirectionsRun,
  History,
  Insights,
  LocalFireDepartment,
  MonitorHeart,
  RestaurantMenu,
  Spa,
  Timeline,
  WaterDrop,
} from '@mui/icons-material';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { tokens } from '../../../theme';
import {
  computeActivityAnalytics,
  computeBiometricsAnalytics,
  computeFastingAnalytics,
  computeInsightsAnalytics,
  computeNutritionAnalytics,
  computeProtocolAnalytics,
} from '../../../utils/userAnalytics';
import UserDashboardSettings from '../UserDashboardSettings';
import AchievementsPanel from '../AchievementsPanel';
import FoodLogPanel from '../FoodLogPanel';
import SupportPanel from '../SupportPanel';
import cardMountains from '../../../assets/card-mountains.png';
import ctaBackground from '../../../assets/cta-background.png';
import landingForest from '../../../assets/landing-forest.png';
import {
  AnalyticsHero,
  AnalyticsScreen,
  ChartPanel,
  InsightCallout,
  MetricTile,
  analyticsTokens,
  chartTooltipStyle,
  panelSx,
} from './shared';

const PIE_COLORS = ['#a9bf7a', '#c98b67', '#8ec8d7', '#d0b47e', '#d58a5f'];

function formatSleep(minutes) {
  if (!minutes) return '—';
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export function FastingArcAnalytics({ metricsRange, onManageFast, fastingActive }) {
  const data = useMemo(() => computeFastingAnalytics(metricsRange), [metricsRange]);

  return (
    <AnalyticsScreen>
      <AnalyticsHero
        title="Fasting Arc Analytics"
        subtitle="Protocol adherence, completion rates, duration trends, and weekly fasting rhythm."
        backgroundImage={landingForest}
        chips={[`${data.completionRate}% completion`, `${data.avgDuration}h avg duration`]}
        actions={
          <Button variant="outlined" onClick={onManageFast} sx={{ color: tokens.cream, borderColor: analyticsTokens.border }}>
            {fastingActive ? 'Manage active fast' : 'Start a fast'}
          </Button>
        }
      />
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={6} md={3}><MetricTile label="Completion rate" value={`${data.completionRate}%`} footnote="30-day window" icon={<LocalFireDepartment fontSize="small" />} accent="#e2b35f" /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Avg duration" value={`${data.avgDuration}h`} footnote="Per session" icon={<Timeline fontSize="small" />} /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Sessions logged" value={data.sessions.length} footnote="With fasting data" icon={<History fontSize="small" />} /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Protocols used" value={data.protocolBreakdown.length} footnote="Unique windows" accent={tokens.clay} icon={<Spa fontSize="small" />} /></Grid>
      </Grid>
      <Grid container spacing={1.5}>
        <Grid item xs={12} lg={8}>
          <ChartPanel title="Adherence trend" subtitle="Session adherence % and hours fasted">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data.adherenceTrend}>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                <XAxis dataKey="date" stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <YAxis yAxisId="left" stroke={tokens.textMuted} tickLine={false} axisLine={false} domain={[0, 100]} />
                <YAxis yAxisId="right" orientation="right" stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend />
                <Bar yAxisId="right" dataKey="hours" fill={tokens.clay} name="Hours" />
                <Line yAxisId="left" type="monotone" dataKey="adherence" stroke={tokens.accentStrong} strokeWidth={2.2} name="Adherence %" dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
        <Grid item xs={12} lg={4}>
          <ChartPanel title="Protocol mix" subtitle="Sessions by fasting window">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.protocolBreakdown} dataKey="count" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {data.protocolBreakdown.map((_, index) => (
                    <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
        <Grid item xs={12}>
          <ChartPanel title="Weekly fasting heatmap" subtitle="Completed (2) · Active (1) · None (0)" height={200}>
            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(data.weeklyHeatmap.length, 30)}, minmax(0, 1fr))`, gap: 0.5, height: '100%', alignItems: 'end' }}>
              {data.weeklyHeatmap.map((day) => (
                <Box key={day.date} sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      height: 48 + day.value * 28,
                      backgroundColor: day.value === 2 ? 'rgba(169,191,122,0.75)' : day.value === 1 ? 'rgba(226,179,95,0.65)' : 'rgba(255,255,255,0.08)',
                      border: `1px solid ${analyticsTokens.border}`,
                      mb: 0.5,
                    }}
                  />
                  <Typography variant="caption" sx={{ color: tokens.textMuted, fontSize: '0.6rem' }}>{day.day}</Typography>
                </Box>
              ))}
            </Box>
          </ChartPanel>
        </Grid>
      </Grid>
    </AnalyticsScreen>
  );
}

export function NutritionAnalytics({
  metricsRange,
  userData,
  nutritionPreferences,
  onLogFood,
  foodEntries = [],
  selectedDateLabel,
  onDeleteFood,
  deletingFood = false,
  onSubmitProduct,
}) {
  const data = useMemo(
    () => computeNutritionAnalytics(metricsRange, userData?.dailyCalorieRequirement, nutritionPreferences),
    [metricsRange, userData, nutritionPreferences]
  );
  const hasCalorieGoal = data.goal != null;
  const hasProteinTarget = data.macroTargets.protein != null;
  const hasCarbTarget = data.macroTargets.carbs != null;

  return (
    <AnalyticsScreen>
      <AnalyticsHero
        title="Nutrition Analytics"
        subtitle="Calorie trends, macro balance, meal distribution, and goal adherence over 30 days."
        backgroundImage={landingForest}
        chips={[
          `${data.avgCalories} kcal avg`,
          data.adherence.calories != null ? `${data.adherence.calories}% calorie adherence` : 'No calorie goal set',
        ]}
        actions={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={onLogFood} sx={{ color: tokens.cream, borderColor: analyticsTokens.border }}>
              Log food
            </Button>
            {onSubmitProduct && (
              <Button variant="outlined" onClick={onSubmitProduct} sx={{ color: tokens.cream, borderColor: analyticsTokens.border }}>
                Submit product
              </Button>
            )}
          </Stack>
        }
      />
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={6} md={3}><MetricTile label="Avg calories" value={data.avgCalories} footnote={hasCalorieGoal ? `Goal ${data.goal}` : 'No goal set'} icon={<RestaurantMenu fontSize="small" />} /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Avg protein" value={`${data.avgMacros.protein}g`} footnote={hasProteinTarget ? `Target ${data.macroTargets.protein}g` : 'No target set'} accent="#a6c26f" /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Avg carbs" value={`${data.avgMacros.carbs}g`} footnote={hasCarbTarget ? `Target ${data.macroTargets.carbs}g` : 'No target set'} accent="#cfb66c" /></Grid>
        <Grid item xs={6} md={3}>
          <MetricTile
            label="Protein adherence"
            value={data.adherence.protein != null ? `${data.adherence.protein}%` : '—'}
            footnote={hasProteinTarget ? 'Days ≥80% target' : 'Set a diet goal to track this'}
            accent={tokens.clay}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1.5}>
        <Grid item xs={12} lg={8}>
          <ChartPanel title="Calorie trend vs goal" subtitle="Daily intake with goal reference">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data.daily}>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                <XAxis dataKey="date" stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <YAxis stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend />
                <Bar dataKey="calories" fill={tokens.accentStrong} name="Calories" />
                {hasCalorieGoal && (
                  <Line type="monotone" dataKey={() => data.goal} stroke={tokens.clay} strokeDasharray="4 4" name="Goal" dot={false} />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
        <Grid item xs={12} lg={4}>
          <ChartPanel title="Meal distribution" subtitle="Total calories by meal type">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.mealDistribution} dataKey="calories" nameKey="meal" innerRadius={50} outerRadius={85}>
                  {data.mealDistribution.map((_, index) => (
                    <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
        <Grid item xs={12}>
          <ChartPanel title="Macro trend" subtitle="Daily protein, carbs, and fats">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.daily}>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                <XAxis dataKey="date" stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <YAxis stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend />
                <Area type="monotone" dataKey="protein" stackId="1" stroke="#a6c26f" fill="#a6c26f" fillOpacity={0.5} />
                <Area type="monotone" dataKey="carbs" stackId="1" stroke="#cfb66c" fill="#cfb66c" fillOpacity={0.5} />
                <Area type="monotone" dataKey="fats" stackId="1" stroke="#d58a5f" fill="#d58a5f" fillOpacity={0.5} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
        <Grid item xs={12}>
          <FoodLogPanel
            foodEntries={foodEntries}
            selectedDateLabel={selectedDateLabel}
            onDeleteFood={onDeleteFood}
            deleting={deletingFood}
          />
        </Grid>
      </Grid>
    </AnalyticsScreen>
  );
}

export function ActivityAnalytics({ metricsRange, userData, currentHabits, onLogActivity }) {
  const data = useMemo(
    () => computeActivityAnalytics(metricsRange, userData, currentHabits),
    [metricsRange, userData, currentHabits]
  );

  return (
    <AnalyticsScreen>
      <AnalyticsHero
        title="Activity Analytics"
        subtitle="Movement consistency, hydration patterns, exercise volume, and weekday performance."
        backgroundImage={ctaBackground}
        chips={[`${data.totals.steps.toLocaleString()} total steps`, `${data.goalHitRate.water}% hydration goal rate`]}
        actions={onLogActivity ? (
          <Button variant="outlined" onClick={onLogActivity} sx={{ color: tokens.cream, borderColor: analyticsTokens.border }}>
            Log activity
          </Button>
        ) : null}
      />
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={6} md={3}><MetricTile label="Steps goal rate" value={`${data.goalHitRate.steps}%`} footnote={`Goal ${data.stepGoal.toLocaleString()}`} icon={<DirectionsRun fontSize="small" />} /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Hydration rate" value={`${data.goalHitRate.water}%`} footnote={`Goal ${data.waterGoal}L`} icon={<WaterDrop fontSize="small" />} accent="#8ec8d7" /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Exercise rate" value={`${data.goalHitRate.exercise}%`} footnote={`Goal ${data.exerciseGoal}h`} accent={tokens.clay} /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Total exercise" value={`${data.totals.exercise} min`} footnote="30-day sum" icon={<DirectionsRun fontSize="small" />} /></Grid>
      </Grid>
      <Grid container spacing={1.5}>
        <Grid item xs={12} lg={8}>
          <ChartPanel title="Activity multi-track" subtitle="Steps, water (L), and exercise (min)">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.daily}>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                <XAxis dataKey="date" stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <YAxis stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend />
                <Line type="monotone" dataKey="steps" stroke={tokens.accentStrong} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="water" stroke="#8ec8d7" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="exercise" stroke={tokens.clay} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
        <Grid item xs={12} lg={4}>
          <ChartPanel title="Weekday pattern" subtitle="Average steps & water by day of week">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.weekdayPattern}>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                <XAxis dataKey="day" stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <YAxis stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend />
                <Bar dataKey="steps" fill={tokens.accentStrong} />
                <Bar dataKey="water" fill="#8ec8d7" />
              </BarChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
        <Grid item xs={12}>
          <Stack spacing={1}>
            {[
              ['Steps goals hit', data.goalHitRate.steps],
              ['Hydration goals hit', data.goalHitRate.water],
              ['Exercise goals hit', data.goalHitRate.exercise],
            ].map(([label, value]) => (
              <Box key={label} sx={{ p: 1.2, border: `1px solid ${analyticsTokens.border}`, backgroundColor: 'rgba(255,255,255,0.02)' }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="body2" sx={{ color: tokens.textSoft }}>{label}</Typography>
                  <Typography variant="body2" sx={{ color: tokens.accentStrong }}>{value}%</Typography>
                </Stack>
                <Box sx={{ height: 6, backgroundColor: 'rgba(255,255,255,0.06)' }}>
                  <Box sx={{ width: `${value}%`, height: '100%', backgroundColor: tokens.accentStrong }} />
                </Box>
              </Box>
            ))}
          </Stack>
        </Grid>
      </Grid>
    </AnalyticsScreen>
  );
}

export function BiometricsAnalytics({ healthRange, userData }) {
  const data = useMemo(() => computeBiometricsAnalytics(healthRange, userData), [healthRange, userData]);

  return (
    <AnalyticsScreen>
      <AnalyticsHero
        title="Biometrics Analytics"
        subtitle="Weight trajectory, heart rate variability, resting heart rate, and sleep quality over time."
        backgroundImage={cardMountains}
        chips={[
          data.weightDelta != null ? `${data.weightDelta > 0 ? '+' : ''}${data.weightDelta} kg trend` : 'Weight trend pending',
          data.avgHrv ? `${data.avgHrv} ms avg HRV` : 'HRV pending',
        ]}
      />
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={6} md={3}><MetricTile label="Current weight" value={data.latest.weight ? `${data.latest.weight} kg` : '—'} footnote="Latest reading" icon={<MonitorHeart fontSize="small" />} /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Avg HRV" value={data.avgHrv ? `${data.avgHrv} ms` : '—'} footnote="Recovery signal" accent="#8ec8d7" /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Avg sleep" value={formatSleep(data.avgSleep)} footnote="Nightly duration" accent="#a6c26f" /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Resting HR" value={data.latest.restingHeartRate ? `${data.latest.restingHeartRate} bpm` : '—'} footnote="Latest" accent={tokens.clay} /></Grid>
      </Grid>
      <Grid container spacing={1.5}>
        {[
          { key: 'weight', title: 'Weight trend', color: '#d0b47e', unit: 'kg' },
          { key: 'hrv', title: 'HRV trend', color: '#8ec8d7', unit: 'ms' },
          { key: 'restingHeartRate', title: 'Resting heart rate', color: '#d58a5f', unit: 'bpm' },
          { key: 'sleepMinutes', title: 'Sleep duration', color: '#a6c26f', unit: 'min' },
        ].map((chart) => (
          <Grid item xs={12} md={6} key={chart.key}>
            <ChartPanel title={chart.title} subtitle={`${chart.unit} over time`} height={240}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.series}>
                  <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                  <XAxis dataKey="date" stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                  <YAxis stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={chartTooltipStyle} />
                  <Area type="monotone" dataKey={chart.key} stroke={chart.color} fill={chart.color} fillOpacity={0.15} strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartPanel>
          </Grid>
        ))}
      </Grid>
    </AnalyticsScreen>
  );
}

export function HistoryAnalytics({ metricsRange, userData }) {
  const fasting = useMemo(() => computeFastingAnalytics(metricsRange), [metricsRange]);
  const streak = userData?.currentFastingStreak || 0;
  const best = Math.max(userData?.bestFastingStreak || 0, streak);

  return (
    <AnalyticsScreen>
      <AnalyticsHero
        title="History Analytics"
        subtitle="Fasting session archive, streak milestones, and duration distribution."
        backgroundImage={ctaBackground}
        chips={[`${streak} day streak`, `${best} day best`]}
      />
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={6} md={3}><MetricTile label="Current streak" value={`${streak} days`} footnote="Active run" icon={<History fontSize="small" />} /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Best streak" value={`${best} days`} footnote="Personal record" accent="#e2b35f" /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Sessions" value={fasting.sessions.length} footnote="Logged in period" icon={<Timeline fontSize="small" />} /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Early endings" value={fasting.sessions.filter((s) => s.endedEarly).length} footnote="Ended before plan" accent={tokens.clay} /></Grid>
      </Grid>
      <Grid container spacing={1.5}>
        <Grid item xs={12} lg={7}>
          <ChartPanel title="Session duration" subtitle="Hours fasted per session">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={fasting.sessions}>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                <XAxis dataKey="date" stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <YAxis stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Bar dataKey="hours" fill={tokens.accentStrong} />
              </BarChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Box sx={panelSx({ p: 2, height: '100%' })}>
            <Typography variant="h5" sx={{ color: tokens.cream, mb: 1.5 }}>Session log</Typography>
            <Stack spacing={0.8} sx={{ maxHeight: 280, overflowY: 'auto' }}>
              {fasting.sessions.map((session) => (
                <Box key={session.rawDate} sx={{ p: 1.1, border: `1px solid ${analyticsTokens.border}`, backgroundColor: 'rgba(255,255,255,0.02)' }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ color: tokens.cream }}>{session.date}</Typography>
                    <Chip label={session.protocol} size="small" sx={{ borderRadius: 2 }} />
                  </Stack>
                  <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                    {session.hours}h · {session.completed ? 'Completed' : 'Active'} {session.endedEarly ? '· Ended early' : ''}
                  </Typography>
                </Box>
              ))}
              {fasting.sessions.length === 0 && (
                <Typography variant="body2" sx={{ color: tokens.textMuted }}>No fasting sessions in this period.</Typography>
              )}
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </AnalyticsScreen>
  );
}

export function ProtocolsAnalytics({ metricsRange, onManageFast, fastingActive, currentProtocol }) {
  const data = useMemo(() => computeProtocolAnalytics(metricsRange), [metricsRange]);

  return (
    <AnalyticsScreen>
      <AnalyticsHero
        title="Protocol Analytics"
        subtitle="Compare fasting windows by success rate, average duration, and session volume."
        backgroundImage={cardMountains}
        chips={[`Active: ${currentProtocol || 'None'}`, `${data.protocolStats.length} protocols used`]}
        actions={
          <Button variant="contained" onClick={onManageFast}>
            {fastingActive ? 'End fast' : 'Start protocol'}
          </Button>
        }
      />
      <Grid container spacing={1.5}>
        {data.protocolStats.map((item, index) => (
          <Grid item xs={12} md={6} key={item.protocol}>
            <Box sx={panelSx({ p: 2 })}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h5" sx={{ color: tokens.cream }}>{item.protocol}</Typography>
                <Chip label={`${item.successRate}% success`} size="small" sx={{ borderRadius: 2, color: PIE_COLORS[index % PIE_COLORS.length] }} />
              </Stack>
              <Typography variant="body2" sx={{ color: tokens.textMuted, mb: 1.5 }}>
                {item.sessions} sessions · {item.completed} completed · {item.avgHours}h average
              </Typography>
              <Box sx={{ height: 6, backgroundColor: 'rgba(255,255,255,0.06)' }}>
                <Box sx={{ width: `${item.successRate}%`, height: '100%', backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }} />
              </Box>
            </Box>
          </Grid>
        ))}
        {data.protocolStats.length === 0 && (
          <Grid item xs={12}>
            <InsightCallout title="No protocol data yet" body="Start logging fasts to unlock protocol performance comparisons and recommendations." />
          </Grid>
        )}
        <Grid item xs={12}>
          <ChartPanel title="Protocol session volume" subtitle="Number of sessions per fasting window">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.protocolStats} layout="vertical">
                <CartesianGrid stroke="rgba(255,255,255,0.08)" horizontal={false} />
                <XAxis type="number" stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="protocol" stroke={tokens.textMuted} tickLine={false} axisLine={false} width={60} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Bar dataKey="sessions" fill={tokens.accentStrong} />
              </BarChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
      </Grid>
    </AnalyticsScreen>
  );
}

export function InsightsAnalytics({ metricsRange, healthRange, userData, userId, nutritionPreferences }) {
  const data = useMemo(
    () => computeInsightsAnalytics(metricsRange, healthRange, userData, nutritionPreferences),
    [metricsRange, healthRange, userData, nutritionPreferences]
  );

  return (
    <AnalyticsScreen>
      <AnalyticsHero
        title="Insights"
        subtitle="Cross-metric intelligence — correlations, anomalies, and weekly performance summaries."
        backgroundImage={landingForest}
        chips={[`${data.correlations.length} active insights`]}
      />
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        {data.summary.map((item) => (
          <Grid item xs={6} md={3} key={item.label}>
            <MetricTile label={item.label} value={item.value} footnote={item.unit || '30-day view'} icon={<Insights fontSize="small" />} />
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={1.5}>
        <Grid item xs={12} md={6}>
          <ChartPanel title="Nutrition vs activity" subtitle="Calories and steps overlay">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data.nutrition.daily.map((n, i) => ({
                date: n.date,
                calories: n.calories,
                steps: data.activity.daily[i]?.steps || 0,
              }))}>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                <XAxis dataKey="date" stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <YAxis yAxisId="left" stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <YAxis yAxisId="right" orientation="right" stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend />
                <Bar yAxisId="left" dataKey="calories" fill={tokens.clay} name="Calories" />
                <Line yAxisId="right" type="monotone" dataKey="steps" stroke={tokens.accentStrong} name="Steps" dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartPanel title="Fasting adherence" subtitle="Daily adherence percentage">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.fasting.adherenceTrend}>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                <XAxis dataKey="date" stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <YAxis domain={[0, 100]} stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Area type="monotone" dataKey="adherence" stroke="#e2b35f" fill="#e2b35f" fillOpacity={0.15} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
        <Grid item xs={12}>
          <Box sx={panelSx({ p: 2 })}>
            <Typography variant="h5" sx={{ color: tokens.cream, mb: 1.5 }}>Intelligence feed</Typography>
            <Stack spacing={1}>
              {data.correlations.map((insight) => (
                <InsightCallout key={insight} title="Pattern detected" body={insight} />
              ))}
              {data.correlations.length === 0 && (
                <InsightCallout title="Building your profile" body="Log more meals, fasts, and activity to unlock personalized cross-metric insights." />
              )}
            </Stack>
          </Box>
        </Grid>
        {userId && (
          <Grid item xs={12}>
            <Box sx={panelSx({ p: 2 })}>
              <AchievementsPanel userId={userId} />
            </Box>
          </Grid>
        )}
      </Grid>
    </AnalyticsScreen>
  );
}

export function SettingsAnalytics({ onNavigateSupport, onOpenProductSubmission }) {
  return <UserDashboardSettings onNavigateSupport={onNavigateSupport} onOpenProductSubmission={onOpenProductSubmission} />;
}

export function SupportAnalytics() {
  return <SupportPanel />;
}

const USER_SCREEN_MAP = {
  'fasting-arc': FastingArcAnalytics,
  nutrition: NutritionAnalytics,
  activity: ActivityAnalytics,
  biometrics: BiometricsAnalytics,
  history: HistoryAnalytics,
  protocols: ProtocolsAnalytics,
  insights: InsightsAnalytics,
  support: SupportAnalytics,
  settings: SettingsAnalytics,
};

export function renderUserAnalyticsScreen(section, props) {
  const Screen = USER_SCREEN_MAP[section];
  if (!Screen) return null;
  return <Screen {...props} />;
}

export const USER_ANALYTICS_SECTIONS = Object.keys(USER_SCREEN_MAP);
